
import ReactPaginate from 'react-paginate';
import { toast } from 'sonner';
import { PageHeader, Card, Badge, Btn, Checkbox, Chip, SegmentedControl, SearchBox, Select, Input, Avatar, BmModal, Loading, EmptyState } from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';
import '../../ForBrands/brandMode.css';
import { openAsUser, isImpersonating } from '../../../auth/impersonation';
import { useReactTable, getCoreRowModel, getPaginationRowModel, flexRender, getFilteredRowModel, getSortedRowModel } from '@tanstack/react-table';
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../../constants';
import { calculateProfileCompletion, calculateStatisticsCompletion, calculateUserRole, extractPositionAbbreviation, getProfileCompletionInfo } from '../../../utils/utils';
import { fetchAllUsers, clearAllUsers, updateUserPermission, computeSocialScore, toggleAdminSignupNotify, PERMISSION_SAVE_FAILED_MESSAGE } from '../../../store/actions/userActions';
import CoachTeamsDialog from './CoachTeamsDialog';
import { coachEmailUnproven, EMAIL_UNPROVEN_HINT, EMAIL_UNPROVEN_LABEL, refusalMessage } from './coachProof';
import AssignAthleteTeamDialog from './AssignAthleteTeamDialog';
import LinkedCerebroRecordsDialog from './LinkedCerebroRecordsDialog';
import TransferAccountDialog, {
  ClaimLinkDialog, TransferPendingBadge, CLAIM_LINK_FAILED, LINK_COPIED,
  canTransfer, copyText, isPilotRow, requestClaimLink, transferErrorMessage, transferPending,
} from './TransferAccountDialog';
import { fetchAllCodes } from '../../../store/actions/codeActions';
import PermissionDialog from './PermissionDialog';
import AssignDialog from './AssignDialog';
import InviteAdminDialog from './InviteAdminDialog';
import DuplicatePairsPanel from './DuplicatePairsPanel';
import { CompletionPair, pctDot, pctTone } from './completion';
import { RowCheckbox, USERS_PAGINATE_CLASSES } from './usersTable';
import PlanBadge from '../../common/PlanBadge';
import { fetchUserPlans } from '../../admin/userPlans';
import { displayTeamName } from '../../../utils/teamNames';
import { fmtIndex10 } from '../../../utils/indexScore';
import CascadePicker from '../../ForBusiness/Cacheshare/CascadePicker';
import {
  executeFetchPlan,
} from '../../ForBusiness/Cacheshare/services/cacheShareServices';
import { resolveFetchPlan } from '../../ForBusiness/Cacheshare/taxonomy';
import {
  verifyCoach as verifyCoachApi,
  unverifyCoach as unverifyCoachApi,
  rejectCoach as rejectCoachApi,
} from '../../auth/Onboarding/services/onboardingServices';

// V2 tones for roles and states (BrandUI Badge tones).
const ROLE_TONE = {
  'Athlete': 'brand', 'Coach': 'dark', 'Brand/Agency': 'soft', 'Rep / Agent': 'soft', 'Admin': 'outline',
  'Cache AI': 'outline', 'Intern': 'passive', 'Supporter': 'passive', 'Recruiter/Scout': 'passive', 'AD/PD': 'passive',
};
const iconBtn = 'inline-flex h-8 w-8 items-center justify-center rounded-full text-bm-muted transition-colors hover:bg-bm-subtle hover:text-bm-ink disabled:opacity-50';

export const ATHLETE_STATUS_FAILED_MESSAGE = 'Could not update the athlete status.';

// The cohort tag a row carries, as staff read it: nothing for live accounts
// and for admins (login lets an admin in whatever the tag says), else
// Pilot, Archived or Merged. The account's own tag decides, as in the
// server's cohort filter, and a row with no tag is a pilot row, the way that
// filter and the analytics backfill count it.
export const cohortBadgeFor = (row) => {
  if ((row?.permissions || []).includes('IS_ADMIN')) return null;
  const merged = row?.mergedInto || row?.athlete?.mergedInto;
  if (merged) return { label: 'Merged', tone: 'passive', title: 'Merged into another profile' };
  const cohort = String(row?.cohort || 'pilot').toLowerCase();
  if (cohort === 'live') return null;
  if (cohort === 'archived') return { label: 'Archived', tone: 'passive', title: 'Replaced by a newer account' };
  return { label: 'Pilot', tone: 'soft', title: 'Pilot record from before the current sign-up. It cannot sign in.' };
};

const CohortBadge = ({ row }) => {
  const b = cohortBadgeFor(row);
  return b ? <span title={b.title}><Badge tone={b.tone}>{b.label}</Badge></span> : null;
};

// The message to show for a refused verification change: the server's
// `message`, else an `error` or `detail` sentence (never a bare code), else
// a plain fallback.
export const athleteStatusErrorMessage = async (res) => {
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  const text = (v) => (typeof v === 'string' ? v.trim() : '');
  const sentence = [text(data?.error), text(data?.detail)].find((v) => v && !/^[a-z0-9_.-]+$/.test(v));
  return text(data?.message) || sentence || ATHLETE_STATUS_FAILED_MESSAGE;
};

// Vertical list of every action for one user, with a confirmation step for
// the ones that change their account. Opened from the row's "More" button.
// `plan` is the account's plan from the server (PlanBadge).
const RowActionsModal = ({ user, roleName, actions, onClose, plan }) => {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const run = async (action) => {
    setBusy(true);
    try {
      await action.run();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'That did not go through.');
    } finally {
      setBusy(false);
      setPending(null);
    }
  };
  const title = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  return (
    <BmModal
      title={pending ? pending.confirm.title : title}
      onClose={() => (busy ? null : pending ? setPending(null) : onClose())}
      width={460}
      footer={pending ? (
        <>
          <Btn variant="secondary" onClick={() => setPending(null)} disabled={busy}>Cancel</Btn>
          <Btn onClick={() => run(pending)} disabled={busy} className={pending.tone === 'danger' ? '!bg-bm-critical hover:!bg-[#c41d14]' : ''}>
            {busy ? 'Working...' : pending.confirm.confirmLabel || 'Confirm'}
          </Btn>
        </>
      ) : (
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
      )}
    >
      {pending ? (
        <p className="bm-body-m text-bm-ink">{pending.confirm.body}</p>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2 bm-body-s text-bm-muted">
            <Badge tone={ROLE_TONE[roleName] || 'outline'}>{roleName}</Badge>
            {user.locked && <Badge tone="critical">Locked</Badge>}
            <TransferPendingBadge row={user} />
            <PlanBadge plan={plan} admin compact />
            <span className="truncate">{user.email}</span>
          </div>
          <div className="flex flex-col divide-y divide-bm-border rounded-[8px] border border-bm-border">
            {actions.map((a) => {
              return (
                <button
                  key={a.key}
                  type="button"
                  disabled={a.disabled || busy}
                  onClick={() => (a.confirm ? setPending(a) : run(a))}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left bm-body-m transition-colors hover:bg-bm-subtle disabled:cursor-not-allowed disabled:opacity-50 ${
                    a.tone === 'danger' ? 'text-bm-critical' : 'text-bm-ink'
                  }`}
                >
                  {a.icon && <Icon name={a.icon} size={16} className={a.tone === 'danger' ? 'text-bm-critical' : 'text-bm-muted'} />}
                  <span className="min-w-0 flex-1">
                    <span className="block">{a.label}</span>
                    {a.hint && <span className="block bm-body-s text-bm-muted">{a.hint}</span>}
                  </span>
                  {a.confirm && <span className="bm-label-xs text-bm-tertiary">confirm</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </BmModal>
  );
};

const EMPTY_CASCADE_FILTERS = {
  sport: null,
  gender: null,
  tier: null,
  region: null,
  body: null,
  subDivision: null,
  classDivision: null,
  conference: null,
  teamSearch: '',
};

const AdminUserManager = () => {
  const user = useSelector((state) => state.user.user || {});
  const userPermissions = user.permissions || [];
  const myEmail = user.email || '';
  const hasAnyPermission = (perms, userPermissions) => perms.some(perm => userPermissions.includes(perm));

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(200);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const users = useSelector((state) => state.user?.data || []);
  const codes = useSelector((state) => state.codes?.allCodes || []);
  const permissions = codes.filter(code => code.type === 'permission').map(item => item.name);
  const totalCounts = useSelector((state) => state.user?.totalCounts || 0);
  const bucketCounts = useSelector(
    (state) => state.user?.bucketCounts || {
      all: 0, athletes: 0, coaches: 0, pending: 0, uncompleted: 0, completed: 0, admins: 0,
    },
  );
  // The server takes role changes and admin invites only from an admin's own
  // session, never from an "open as user" tab, so those controls are hidden
  // there. It is set once: a tab never switches in or out of that mode.
  const [impersonating] = useState(() => isImpersonating());
  // Admins only, as the server's invite endpoint. Admins and Cache AI staff
  // still see the Admins list.
  const canInviteAdmins = !impersonating && hasAnyPermission(['IS_ADMIN'], userPermissions);
  // Account transfers too: admins only, from their own session.
  const canTransferAccounts = !impersonating && hasAnyPermission(['IS_ADMIN'], userPermissions);
  const canSeeAdmins = hasAnyPermission(['IS_ADMIN', 'IS_CACHE_AI'], userPermissions);
  const [inviteAdminOpen, setInviteAdminOpen] = useState(false);
  // 'users' = the account table; 'duplicates' = possible duplicate athletes
  // for review (admins and Cache AI staff, as the server's endpoints).
  const canReviewDuplicates = hasAnyPermission(['IS_ADMIN', 'IS_CACHE_AI'], userPermissions);
  const [view, setView] = useState('users');

  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Separate dialog for the coach Manage Teams panel — replaces the
  // generic permissions dialog for coach rows. Admins can add additional
  // teams (program-director access for multiple rosters).
  const [coachTeamsDialogOpen, setCoachTeamsDialogOpen] = useState(false);
  const [coachTeamsTarget, setCoachTeamsTarget] = useState(null);
  // Admin "assign / change team" dialog for athlete rows — force-set or clear
  // an athlete's team (support tool for fixing wrong team assignments).
  const [assignTeamOpen, setAssignTeamOpen] = useState(false);
  const [assignTeamTarget, setAssignTeamTarget] = useState(null);
  // 'add' when the dialog opens to verify an athlete on a team (Verify on a
  // team): the team is added beside any main team. null keeps the dialog's
  // own default.
  const [assignTeamMode, setAssignTeamMode] = useState(null);
  // Admin "Linked Cerebro records" dialog for athlete rows: other Cerebro
  // records of the same player, whose games count as the athlete's.
  const [linkedRecordsTarget, setLinkedRecordsTarget] = useState(null);
  // Admin "Transfer account" for a profile our team built (a cachescore.com
  // login): it moves to the athlete's own email, and they claim it from a
  // link. `claimLinkShown` puts a claim link on screen when the clipboard
  // refused it or the claim email did not send.
  const [transferTarget, setTransferTarget] = useState(null);
  const [claimLinkShown, setClaimLinkShown] = useState(null);
  // Themed confirm dialog for unverifying a coach. Replaces the browser
  // native window.confirm so the destructive action gets the same look as
  // every other modal on this page.
  const [unverifyTarget, setUnverifyTarget] = useState(null);
  const [unverifying, setUnverifying] = useState(false);
  const [rowData, setRowData] = useState([]);

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Quick-filter pill state. Maps to backend /users/all/?role=&status=
  //   'all'         — every non-admin user
  //   'athlete'     — IS_ATHLETE
  //   'coach'       — IS_COACH
  //   'pending'     — IS_ATHLETE + status='unverified'  (awaiting admin review)
  //   'uncompleted' — IS_ATHLETE + status='uncompleted' (profile not finished)
  //   'completed'   — IS_ATHLETE + status='verified'    (fully verified)
  const [bucket, setBucket] = useState('all');
  // Cohort scope. 'live' = post-deploy signups (default); 'pilot' = legacy
  // pre-deploy users; 'all' = both. Mirrors the backend `cohort` param.
  const [cohort, setCohort] = useState('live');
  const bucketToParams = useMemo(() => ({
    all:         { role: 'all',     status: 'all' },
    athlete:     { role: 'athlete', status: 'all' },
    coach:       { role: 'coach',   status: 'all' },
    admin:       { role: 'admin',   status: 'all' },
    pending:     { role: 'athlete', status: 'pending' },
    uncompleted: { role: 'athlete', status: 'uncompleted' },
    completed:   { role: 'athlete', status: 'completed' },
  }), []);

  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedIntern, setSelectedIntern] = useState(null);
  const [previousAssignSelection, setPreviousAssignSelection] = useState({});

  const [loading, setLoading] = useState(false);
  const [computingScoreEmail, setComputingScoreEmail] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const [showUploadTypeDialog, setShowUploadTypeDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  // const [uploadType, setUploadType] = useState(null);
  const [uploadLevel, setUploadLevel] = useState(null);

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadType, setDownloadType] = useState(null);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('');
  const [downloadRole, setDownloadRole] = useState('all');
  const [downloadStatus, setDownloadStatus] = useState('');

  // Cascade filter state (Sport > Tier > Region > Body > League > Conference >
  // Team). Drives both the team list shown in Step 7 and the user-list query.
  const [cascadeFilters, setCascadeFilters] = useState(EMPTY_CASCADE_FILTERS);
  const [cascadeTeam, setCascadeTeam] = useState(null);
  const [cascadeTeams, setCascadeTeams] = useState([]);
  const [cascadeTeamsLoading, setCascadeTeamsLoading] = useState(false);
  const [showCascade, setShowCascade] = useState(false);

  const cascadePlan = useMemo(
    () =>
      resolveFetchPlan({
        sportValue: cascadeFilters.sport?.value,
        gender: cascadeFilters.gender?.value,
        tier: cascadeFilters.tier?.value,
        region: cascadeFilters.region?.value,
        body: cascadeFilters.body,
        subDivision: cascadeFilters.subDivision,
        classDivision: cascadeFilters.classDivision,
        conference: cascadeFilters.conference?.value,
      }),
    [cascadeFilters],
  );
  const cascadePlanKey = useMemo(
    () => (cascadePlan ? JSON.stringify(cascadePlan) : ''),
    [cascadePlan],
  );

  useEffect(() => {
    setCascadeTeam(null);
    if (!cascadePlan) {
      setCascadeTeams([]);
      return;
    }
    let cancelled = false;
    setCascadeTeamsLoading(true);
    // Every saved team, not only those with stats: a paused provider
    // (football while Genius is down) would otherwise hide the teams it has
    // no stats for, and this picks a team to work with, not to score.
    executeFetchPlan(cascadePlan, { allTeams: true })
      .then((teams) => {
        if (!cancelled) setCascadeTeams(teams || []);
      })
      .catch(() => {
        if (!cancelled) setCascadeTeams([]);
      })
      .finally(() => {
        if (!cancelled) setCascadeTeamsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cascadePlanKey]);

  // Resolve cascade selection to user-list query params: list of league
  // sport-keys (mapped from the fetch plan) plus the optional team_id.
  const cascadeQuery = useMemo(() => {
    if (!cascadePlan) return { leagues: [], team_id: null };
    let leagues;
    if (cascadePlan.dataSource === 'sportsradar') {
      leagues = [cascadePlan.srSport];
    } else if (cascadePlan.dataSource === 'cache_teams') {
      // Cache partner teams carry their league_key per-team. Use the
      // selected team's cacheLeague (or its raw league_key) so the
      // user-list filter narrows correctly.
      leagues = cascadeTeam?.cacheLeague
        ? [cascadeTeam.cacheLeague]
        : cascadeTeam?.league_key
          ? [cascadeTeam.league_key]
          : [];
    } else {
      leagues = cascadePlan.cerebroSports || [];
    }
    return { leagues, team_id: cascadeTeam?.id || null };
  }, [cascadePlan, cascadeTeam]);

  const clearCascade = () => {
    setCascadeFilters(EMPTY_CASCADE_FILTERS);
    setCascadeTeam(null);
    setCascadeTeams([]);
  };

  const token = useSelector((state) => state.auth.token); // Needed for auth headers

  // Each row's plan as the server resolves it (Kobi, 2026-10-07): an athlete
  // their team's coach covers is Premium with no tier of their own, and a
  // plan CACHE set up past its end date reads Free. The stored subscription
  // fields say neither. Staff only, from their own session, as the server
  // answers (an intern gets the athletes assigned to them).
  const canReadPlans = !impersonating && hasAnyPermission(['IS_ADMIN', 'IS_CACHE_AI', 'IS_INTERN'], userPermissions);
  const [plans, setPlans] = useState({});
  const planEmails = useMemo(() => rowData.map((u) => u.email).filter(Boolean).sort().join('\n'), [rowData]);
  useEffect(() => {
    if (!canReadPlans || !planEmails) return undefined;
    let live = true;
    Promise.resolve(fetchUserPlans(planEmails.split('\n'), token)).then((found) => {
      if (live && found && Object.keys(found).length) setPlans((prev) => ({ ...prev, ...found }));
    }).catch(() => {});
    return () => { live = false; };
  }, [canReadPlans, planEmails, token]);

  const handleDownloadSample = async () => {
    try {
      let endpoint = '';
      let filename = '';
      
      if (downloadType === 'sample') {
        endpoint = '/api/users/athletes/download-sample-csv/';
        filename = 'sample_template.csv';
      } else if (downloadType === 'profiles') {
        endpoint = '/api/users/athletes/export-csv/';
        filename = 'athletes_profiles.csv';
      } else if (downloadType === 'athlete-profiles') {
        endpoint = '/api/users/athletes/export-profiles/';
        filename = 'athlete_profiles.csv';
      } else {
        toast.error("Please select a download type.");
        return;
      }
      setIsExporting(true);

      // Build query parameters
      const params = new URLSearchParams();
      if (selectedTeamFilter && (downloadType === 'profiles' || downloadType === 'athlete-profiles')) {
        params.append('team', selectedTeamFilter);
      }
      // Role + status filters apply to the full-profiles export only.
      if (downloadType === 'profiles') {
        if (downloadRole && downloadRole !== 'all') params.append('role', downloadRole);
        if (downloadStatus) params.append('status', downloadStatus);
      }
      const queryString = params.toString();
      const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;

      const response = await fetch(`${BASE_URL}${fullEndpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    
      const contentType = response.headers.get('content-type');
      if (!response.ok || !contentType || !contentType.includes('text/csv')) {
        const text = await response.text();
        console.error('Download failed:', {
          status: response.status,
          statusText: response.statusText,
          contentType: contentType,
          responsePreview: text.substring(0, 200)
        });
        toast.error(`Failed to download file. Status: ${response.status}`);
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      const successMessages = {
        'sample': 'Sample template',
        'profiles': 'Athletes profiles',
        'athlete-profiles': 'Athlete profiles'
      };
      toast.success(`${successMessages[downloadType] || 'File'} downloaded successfully.`);
    } catch (error) {
      toast.error("An error occurred while downloading the file.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadClick = () => {
    setDownloadType(null);
    setSelectedTeamFilter('');
    setDownloadRole('all');
    setDownloadStatus('');
    setDownloadOpen(true);
  };

  const cancelDownload = () => {
    setDownloadOpen(false);
    setDownloadType(null);
    setDownloadRole('all');
    setDownloadStatus('');
    setSelectedTeamFilter('');
  };

  const handleDownloadConfirm = () => {
    if (!downloadType) {
      toast.error("Please select a download type.");
      return;
    }
    setDownloadOpen(false);
    handleDownloadSample();
    setDownloadType(null);
    setSelectedTeamFilter('');
    setDownloadRole('all');
    setDownloadStatus('');
  };

  const teamOptions = useMemo(() => {
    return codes
      .filter(code => code.type === 'team')
      .map(code => ({
        value: code.value,
        label: code.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [codes]);

  const handleUploadToEndpoint = async (endpoint, level) => {
    if (!pendingFile) return;
    if (endpoint === '/api/users/coach/csv/upload/' && level !== 'highschool') {
      toast.warning("You can only upload High School Athlete's Statistics.");
      return;
    }
    const formData = new FormData();
    formData.append("file", pendingFile);
    if (level) {
      if (level === 'highschool') {
        formData.append("level", 1);
      } else if (level === 'college') {
        formData.append("level", 2);
      } else if (level === 'pro') {
        formData.append("level", 3);
      }
    } else {
      toast.warning("Please select a level of play.");
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.status === 200) {
        toast.success("File upload started!");
        await dispatch(fetchAllCodes());
        await dispatch(clearAllUsers());
        await dispatch(fetchAllUsers({
          page: 1, limit, search: searchQuery,
          ...bucketToParams[bucket],
          cohort,
        }));
        setPage(1);
      }
    } catch (err) {
      toast.error("File upload failed!");
    }
    setPendingFile(null);
    setUploadLevel(null);
  };

  useEffect(() => {
    setLoading(true);
    dispatch(clearAllUsers());
    dispatch(fetchAllUsers({
      page: 1, limit, search: searchQuery,
      ...bucketToParams[bucket],
      leagues: cascadeQuery.leagues,
      team_id: cascadeQuery.team_id,
      cohort,
    })).finally(() => setLoading(false));
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, limit, searchQuery, bucket, bucketToParams, cohort, cascadeQuery.leagues.join(','), cascadeQuery.team_id]);

  // Auto-fill any MISSING Cache bios when an admin opens the user table, so
  // every athlete ends up with a bio. Fires once per session (the backend is
  // idempotent — it skips athletes that already have one and athletes who chose
  // to write their own). Fire-and-forget: generation runs in the background.
  useEffect(() => {
    if (!userPermissions.includes('IS_ADMIN')) return;
    if (sessionStorage.getItem('bioMissingKicked')) return;
    sessionStorage.setItem('bioMissingKicked', '1');
    const token = localStorage.getItem('token');
    fetch(`${BASE_URL}/api/users/bio/generate-missing/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageClick = (data) => {
    const selected = data.selected + 1;
    setPage(selected);
    setLoading(true);
    dispatch(clearAllUsers());
    dispatch(fetchAllUsers({
      page: selected, limit, search: searchQuery,
      ...bucketToParams[bucket],
      leagues: cascadeQuery.leagues,
      team_id: cascadeQuery.team_id,
      cohort,
    })).finally(() => setLoading(false));
  };

  const navigateViewProfile = (athlete) => {
    localStorage.setItem('athleteEmail', athlete.email);
    navigate(`/business/athlette/profile/${athlete.athlete?.athleteId || ''}`);
  };

  // "Open as user": a new tab signed in as that account, with the pages
  // that user sees and every action available, while this tab stays the
  // admin's own session. Replaces the old admin-mode edit pages.
  const [openingAs, setOpeningAs] = useState(null);
  const [actionsFor, setActionsFor] = useState(null);
  const openUserAs = async (target) => {
    setOpeningAs(target.email);
    try {
      await openAsUser(async () => {
        const res = await fetch(`${BASE_URL}/api/users/admin/impersonate/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ email: target.email }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Could not open this user.');
        return data;
      });
    } catch (err) {
      toast.error(err.message || 'Could not open this user.');
    } finally {
      setOpeningAs(null);
    }
  };

  // Delete confirmation modal state. Holds the user being deleted (or null
  // when the modal is closed). The admin has to type the user's email to
  // arm the destructive button.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingNow, setDeletingNow] = useState(false);

  const requestDeleteUser = (user) => {
    setDeleteConfirmText('');
    setPendingDelete(user);
  };

  const cancelDeleteUser = () => {
    if (deletingNow) return;
    setPendingDelete(null);
    setDeleteConfirmText('');
  };

  const confirmDeleteUser = async () => {
    const user = pendingDelete;
    if (!user) return;
    if ((deleteConfirmText || '').trim().toLowerCase() !== (user.email || '').toLowerCase()) {
      toast.error("Email doesn't match. Type the user's email to confirm.");
      return;
    }
    setDeletingNow(true);
    try {
      const res = await fetch(`${BASE_URL}/api/users/users/${user.email}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (res && res.status === 204) {
        dispatch(clearAllUsers());
        await dispatch(fetchAllUsers({
          page, limit, search: searchQuery,
          ...bucketToParams[bucket],
          cohort,
        }));
        toast.success("User information successfully deleted.");
        setPendingDelete(null);
        setDeleteConfirmText('');
      } else {
        toast.error("User deletion failed!");
      }
    } catch (err) {
      toast.error("User deletion failed!");
    } finally {
      setDeletingNow(false);
    }
  };

  // Every caller (the More list) confirms first in RowActionsModal.
  const handleUpdateAthleteStatus = async (user, newStatus) => {
    if (!user.permissions.includes("IS_ATHLETE")) {
      toast.error("This action is only for athletes.");
      return;
    }

    const res = await fetch(`${BASE_URL}/api/users/users/update-athlete-verification/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ email: user.email, status: newStatus }),
    });

    if (res && res.status === 200) {
      const updatedUser = {
        ...user,
        athlete: { ...user.athlete, status: newStatus },
      };
      toast.success(`${user.name} has been ${newStatus}.`);
      setRowData((prev) => prev.map(u => u.email === updatedUser.email ? updatedUser : u));
      return;
    }
    // Any refusal shows the server's words, such as "This athlete is not
    // assigned to you." for an intern.
    toast.error(await athleteStatusErrorMessage(res));
  };

  // Coach verification — approve / reject the request the coach submitted
  // through onboarding. Approve generates the 6-char code (server side); we
  // patch the row in-place so the column flips from "Verify / Reject" to
  // the green code badge without a full page refresh.
  const handleVerifyCoach = async (user) => {
    try {
      const data = await verifyCoachApi(user.email);
      const updated = {
        ...user,
        coachVerification: {
          status: data?.status || 'verified',
          verificationCode: data?.verificationCode || '',
          submittedAt: data?.submittedAt,
          verifiedAt: data?.verifiedAt,
        },
      };
      setRowData((prev) => prev.map((u) => (u.email === user.email ? updated : u)));
      toast.success(
        data?.verificationCode
          ? `${user.name || user.email} verified. Code: ${data.verificationCode}`
          : `${user.name || user.email} verified.`,
      );
    } catch (err) {
      // A coach whose email is unproven gets the server's plain reason.
      toast.error(refusalMessage(err, 'Failed to verify coach'));
    }
  };

  // Open the themed unverify-confirmation panel for this coach.
  const handleUnverifyCoach = (user) => setUnverifyTarget(user);

  const confirmUnverifyCoach = async () => {
    const user = unverifyTarget;
    if (!user) return;
    setUnverifying(true);
    try {
      const data = await unverifyCoachApi(user.email);
      const updated = {
        ...user,
        coachVerification: {
          status: data?.status || 'pending',
          verificationCode: '',
          submittedAt: data?.submittedAt,
          verifiedAt: null,
        },
      };
      setRowData((prev) => prev.map((u) => (u.email === user.email ? updated : u)));
      toast.success(`${user.name || user.email}'s verification revoked.`);
      setUnverifyTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to unverify coach';
      toast.error(msg);
    } finally {
      setUnverifying(false);
    }
  };

  // The More list confirms first in RowActionsModal.
  const handleRejectCoach = async (user) => {
    try {
      const data = await rejectCoachApi(user.email);
      const updated = {
        ...user,
        coachVerification: {
          status: data?.status || 'rejected',
          verificationCode: '',
          submittedAt: data?.submittedAt,
          verifiedAt: data?.verifiedAt,
        },
      };
      setRowData((prev) => prev.map((u) => (u.email === user.email ? updated : u)));
      toast.success(`${user.name || user.email}'s request rejected.`);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to reject coach';
      toast.error(msg);
    }
  };

  const handleComputeSocialScore = async (user) => {
    const hasSocials = user.athlete?.socialMedia?.length > 0;
    if (!hasSocials) {
      toast.error("This athlete has no social media URLs on their profile.");
      return;
    }
    setComputingScoreEmail(user.email);
    try {
      const result = await dispatch(computeSocialScore(user.email));
      // The pillar out of 10, as every page shows it ("6.2/10", 10/19 notes, D11).
      toast.success(`Visibility score computed for ${user.name}: ${fmtIndex10(result.social_media_score, { suffix: true })}`);
    } catch (err) {
      toast.error(`Failed to compute score for ${user.name}: ${err.message}`);
    } finally {
      setComputingScoreEmail(null);
    }
  };

  const handleAssignInterns = async (totalEmails, checkedEmails) => {
    try {
      const res = await fetch(`${BASE_URL}/api/users/users/assign-athlete-to-interns/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ selectedIntern, totalEmails, checkedEmails })
      });
  
      if (res.ok) {
        toast.success("Athletes successfully assigned to intern(s).");
      } else {
        toast.error("Assignment failed.");
      }
      
      dispatch(clearAllUsers());
      dispatch(fetchAllUsers({
        page, limit, search: searchQuery,
        ...bucketToParams[bucket],
        cohort,
      }));

    } catch (err) {
      toast.error("An error occurred during assignment.");
    }
  };  

  useEffect(() => {
    // Other buckets hide the signed-in admin from their own table (no
    // point self-managing in the main flow). On the Admins bucket we
    // surface self so the count and the rows match — the action cell
    // still suppresses destructive buttons for the self row.
    // The cohort option (Archived included) is applied by the server, which
    // ignores it on the Admins bucket, so the rows here always match the
    // chip counts.
    setRowData(
      bucket === 'admin'
        ? users
        : users.filter((u) => u.email !== myEmail),
    );
  }, [users, myEmail, bucket]);

  // Per-admin signup-alert toggle (Admins bucket). Optimistic update with
  // revert on failure.
  const handleToggleSignupNotify = async (targetUser, enabled) => {
    setRowData((prev) => prev.map((u) => (
      u.email === targetUser.email ? { ...u, notifyOnSignup: enabled } : u
    )));
    try {
      await dispatch(toggleAdminSignupNotify(targetUser.email, enabled));
      toast.success(`Signup alerts ${enabled ? 'on' : 'off'} for ${targetUser.email}.`);
    } catch (err) {
      setRowData((prev) => prev.map((u) => (
        u.email === targetUser.email ? { ...u, notifyOnSignup: !enabled } : u
      )));
      toast.error('Could not update notification setting.');
    }
  };

  // Lock or unlock an account: a locked account cannot sign in by password,
  // mobile code or Google and its session ends within a minute; its data is
  // untouched. Admins only, never on yourself.
  const handleLockUser = async (targetUser, locked) => {
    const res = await fetch(`${BASE_URL}/api/users/admin/users/${encodeURIComponent(targetUser.email)}/lock/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ locked }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.message || 'Could not update the lock.');
    setRowData((prev) => prev.map((u) => (
      u.email === targetUser.email ? { ...u, locked: !!data.locked, lockedAt: data.lockedAt, lockedBy: data.lockedBy } : u
    )));
    toast.success(locked ? `${targetUser.email} is locked. They cannot sign in until unlocked.` : `${targetUser.email} is unlocked.`);
  };

  // Reloads the page of the list on screen, every filter kept, without
  // clearing it first.
  const refreshUsers = () => dispatch(fetchAllUsers({
    page, limit, search: searchQuery,
    ...bucketToParams[bucket],
    leagues: cascadeQuery.leagues,
    team_id: cascadeQuery.team_id,
    cohort,
  }));

  // A transfer went through: the row takes the athlete's email and the
  // pending transfer at once, then the list reloads from the server.
  const handleTransferred = (target, data) => {
    const newEmail = data?.newEmail || target.email;
    setRowData((prev) => prev.map((u) => (u.email === target.email ? {
      ...u,
      email: newEmail,
      emailUnproven: true,
      transfer: data?.transfer || { status: 'pending', from: target.email, to: newEmail },
      athlete: u.athlete ? { ...u.athlete, email: newEmail } : u.athlete,
    } : u)));
    refreshUsers();
  };

  // A fresh claim link for a transfer still waiting (and the claim email
  // again when `sendEmail`). A refusal throws the server's words for the
  // More list to show; one that says nothing is waiting any more reloads
  // the list, since the row is out of date.
  const fetchClaimLink = async (target, sendEmail) => {
    let reply;
    try {
      reply = await requestClaimLink(target.email, sendEmail);
    } catch (err) {
      throw new Error(CLAIM_LINK_FAILED);
    }
    const { ok, status, data } = reply;
    if (!ok) {
      if (data.error === 'not_pending') refreshUsers();
      throw new Error(transferErrorMessage(status, data, CLAIM_LINK_FAILED));
    }
    if (!data.claimLink) throw new Error(CLAIM_LINK_FAILED);
    if (data.transfer) {
      setRowData((prev) => prev.map((u) => (u.email === target.email ? { ...u, transfer: data.transfer } : u)));
    }
    return data;
  };

  const handleCopyClaimLink = async (target) => {
    const data = await fetchClaimLink(target, false);
    if (await copyText(data.claimLink)) {
      toast.success(LINK_COPIED);
      return;
    }
    // The browser kept the clipboard (Safari after a request): show the link.
    setClaimLinkShown({ email: data.transfer?.to || target.email, link: data.claimLink });
  };

  const handleResendClaimEmail = async (target) => {
    const data = await fetchClaimLink(target, true);
    const to = data.transfer?.to || target.email;
    if (data.emailSent) {
      toast.success(`Claim email sent to ${to}.`);
      return;
    }
    setClaimLinkShown({ email: to, link: data.claimLink, emailFailed: true });
  };

  // Responsive columns: hide less important columns on mobile
  // Cell helpers: one compact cell per topic (user + email, sport + position,
  // team + school, both completion badges) instead of a column each.
  const codeName = (type, value) => codes.find((c) => c.type === type && c.value === value)?.name || '';
  const sportOf = (row) => {
    const primarySport = row.athlete?.primarySport;
    if (primarySport) return codeName('sport', primarySport);
    return row.coachVerification?.cascade?.sport || '';
  };
  const positionOf = (row) => {
    const primarySport = row.athlete?.primarySport;
    if (!primarySport) return '';
    const positions = codes.find((c) => c.type === 'sport' && c.value === primarySport)?.positions || [];
    const values = Array.isArray(row.athlete?.position) ? row.athlete.position : (row.athlete?.position != null ? [row.athlete.position] : []);
    return values.map((v) => extractPositionAbbreviation(positions.find((pos) => pos.value === v)?.name || '')).filter(Boolean).join(', ');
  };
  // An athlete row's team and school are the server's labels from the
  // athlete's identity (teamLabel: the team they are on, schoolLabel: the
  // institution their level leads with), so a college a team link filed in
  // the Club field reads as their college and a college team never as no
  // team. Rows without them (coaches, older payloads) read the codes. Every
  // name goes through the one display rule (utils/teamNames): no season
  // suffix, the age group of the league key when the team is a squad.
  const teamOf = (row) => {
    if (row.teamLabel != null && row.athlete && Object.keys(row.athlete).length) {
      return displayTeamName({ teamName: row.teamLabel, displayName: row.teamDisplayName, ageGroup: row.teamAgeGroup });
    }
    if (row.athlete?.currentTeam) return displayTeamName({ teamName: codeName('team', row.athlete.currentTeam) });
    const cv = row.coachVerification;
    if (cv?.currentTeam) return displayTeamName({ teamName: cv.currentTeam, league: cv.league, displayName: cv.displayName });
    if (row.options?.currentTeam) return displayTeamName({ teamName: codeName('team', row.options.currentTeam) });
    return '';
  };
  const schoolOf = (row) => {
    if (row.schoolLabel != null) return row.schoolLabel;
    return row.athlete?.previousSchool ? codeName('school', row.athlete.previousSchool) : '';
  };
  const verificationOf = (row) => {
    const perms = row.permissions || [];
    const isAthlete = perms.includes('IS_ATHLETE');
    const isCoach = perms.includes('IS_COACH') || perms.includes('IS_RECRUITER_SCOUT') || perms.includes('IS_BRAND_AGENCY') || perms.includes('IS_AD_PD');
    if (isAthlete) {
      const ath = row.athlete || {};
      const status = ath.status || '';
      const onboardingDone = !!(row.onboardingCompleted || row?.options?.onboardingCompleted || ath.onboardingCompleted);
      const onboarding = status === 'uncompleted' && !onboardingDone;
      if (status === 'verified') {
        const viaCoach = !!ath.coachEmail || !!ath.coachVerificationCode;
        return { label: 'Verified', tone: 'outline', dot: '#1f9d55', onboarding,
                 title: viaCoach ? `Verified by coach${ath.coachEmail ? ` ${ath.coachEmail}` : ''}` : 'Verified manually by an admin' };
      }
      if (status === 'unverified') return { label: 'Pending', tone: 'attention', onboarding, title: 'Profile complete, awaiting coach link or admin verification' };
      if (status === 'uncompleted') return { label: 'Incomplete', tone: 'passive', onboarding, title: onboarding ? 'Athlete is still going through onboarding' : "Athlete hasn't finished their profile yet" };
      return { label: status || 'unknown', tone: 'passive', onboarding, title: status || 'unknown' };
    }
    if (isCoach) {
      const cv = row.coachVerification;
      if (!cv) return { label: 'None', tone: 'passive', title: "Coach hasn't completed onboarding yet" };
      if (cv.status === 'verified') return { label: 'Verified', tone: 'outline', dot: '#1f9d55', title: cv.verificationCode ? `Verified · code ${cv.verificationCode}` : 'Verified by admin' };
      if (cv.status === 'rejected') return { label: 'Rejected', tone: 'critical', title: 'Verification request rejected' };
      return { label: 'Pending', tone: 'attention', title: 'Awaiting admin verification' };
    }
    return null;
  };
  // The one settings action a row gets as an icon; everything else sits in More.
  const settingsActionFor = (u, role) => {
    if (!userPermissions.includes('IS_ADMIN')) return null;
    if (['Coach', 'AD/PD', 'Recruiter/Scout', 'Brand/Agency'].includes(role)) {
      return { title: 'Manage teams', run: () => { setCoachTeamsTarget(u); setCoachTeamsDialogOpen(true); } };
    }
    if (role === 'Athlete') return { title: 'Assign or change team', run: () => { setAssignTeamTarget(u); setAssignTeamMode(null); setAssignTeamOpen(true); } };
    if (['Cache AI', 'Intern'].includes(role)) {
      if (impersonating) return null;
      return { title: 'Permissions', run: () => { setSelectedUser(u); setDialogOpen(true); } };
    }
    return null;
  };
  // Every action for a user, for the More list. Account-changing ones ask
  // for confirmation inside the list.
  const buildRowActions = (u) => {
    const role = calculateUserRole(u?.permissions || []);
    const isAdminRow = (u.permissions || []).includes('IS_ADMIN');
    const isSelf = u.email === myEmail;
    const who = u.name || u.email;
    const out = [];
    if (!isSelf && userPermissions.includes('IS_ADMIN')) {
      out.push({ key: 'open', label: 'Open as user', hint: 'New tab signed in as this account', icon: 'log-in', run: () => openUserAs(u) });
    }
    if (role === 'Athlete' && hasAnyPermission(['IS_ADMIN', 'IS_CACHE_AI', 'IS_COACH'], userPermissions)) {
      out.push({ key: 'view', label: 'View player profile', icon: 'user', run: () => navigateViewProfile(u) });
    }
    const settings = settingsActionFor(u, role);
    if (settings) out.push({ key: 'settings', label: settings.title, icon: 'settings', run: settings.run });
    if (role === 'Athlete' && userPermissions.includes('IS_ADMIN')) {
      out.push({ key: 'cerebro-records', label: 'Linked Cerebro records', hint: 'Other Cerebro records of the same player',
                 icon: 'link', run: () => setLinkedRecordsTarget(u) });
    }
    if (role === 'Athlete' && hasAnyPermission(['IS_ADMIN', 'IS_INTERN'], userPermissions)) {
      if (u.athlete?.status === 'unverified' && userPermissions.includes('IS_ADMIN')) {
        // An admin verifies an athlete by putting them on the team they play
        // on (an admin add counts as verified), so the team's plan or code
        // can include Premium; a bare verified status never does (10/19).
        out.push({ key: 'verify', label: 'Verify on a team', hint: 'Add them to the team they play on', icon: 'check-circle',
                   run: () => { setAssignTeamTarget(u); setAssignTeamMode('add'); setAssignTeamOpen(true); } });
      } else if (u.athlete?.status === 'unverified') {
        // Interns cannot assign teams: their verification stays a status.
        out.push({ key: 'verify', label: 'Mark as verified', icon: 'check-circle',
                   confirm: { title: 'Mark as verified', body: `Mark ${who} as verified? They get verified status without a coach link.`, confirmLabel: 'Mark verified' },
                   run: () => handleUpdateAthleteStatus(u, 'verified') });
      }
      if (u.athlete?.status === 'verified') {
        out.push({ key: 'unverify', label: 'Revoke verification', icon: 'x-circle', tone: 'danger',
                   confirm: { title: 'Revoke verification', body: `Revoke ${who}'s verified status? They return to unverified until a coach or admin verifies them again.`, confirmLabel: 'Revoke' },
                   run: () => handleUpdateAthleteStatus(u, 'unverified') });
      }
    }
    if (role === 'Coach' && userPermissions.includes('IS_ADMIN')) {
      const cv = u.coachVerification;
      // Verifying waits until the coach proves the email (the server says 409 too).
      const unprovenHold = coachEmailUnproven(u) ? { disabled: true, hint: `${EMAIL_UNPROVEN_LABEL}. ${EMAIL_UNPROVEN_HINT}` } : {};
      if (!cv) {
        out.push({ key: 'nocv', label: 'No onboarding submission yet', icon: 'shield', disabled: true, run: () => {} });
      } else if (cv.status === 'pending') {
        out.push({ key: 'verify-coach', label: 'Verify coach and issue code', icon: 'shield', ...unprovenHold,
                   confirm: { title: 'Verify coach', body: `Verify ${who}? They receive a 6-character team code that athletes use to link to their roster.`, confirmLabel: 'Verify' },
                   run: () => handleVerifyCoach(u) });
        out.push({ key: 'reject-coach', label: 'Reject verification request', icon: 'x-circle', tone: 'danger',
                   confirm: { title: 'Reject request', body: `Reject ${who}'s coach verification request? They can resubmit later.`, confirmLabel: 'Reject' },
                   run: () => handleRejectCoach(u) });
      } else if (cv.status === 'verified') {
        out.push({ key: 'copy-code', label: `Copy team code ${cv.verificationCode || ''}`.trim(), icon: 'copy', disabled: !cv.verificationCode,
                   run: () => navigator.clipboard?.writeText(cv.verificationCode).then(() => toast.success(`Code copied: ${cv.verificationCode}`)) });
        out.push({ key: 'unverify-coach', label: 'Revoke verification', icon: 'x-circle', tone: 'danger', run: () => handleUnverifyCoach(u) });
      } else if (cv.status === 'rejected') {
        out.push({ key: 'reverify-coach', label: 'Re-verify coach', icon: 'shield', ...unprovenHold,
                   confirm: { title: 'Re-verify coach', body: `Clear the rejection and verify ${who}? They receive a team code.`, confirmLabel: 'Verify' },
                   run: () => handleVerifyCoach(u) });
      }
    }
    if (role === 'Intern' && hasAnyPermission(['IS_ADMIN', 'IS_CACHE_AI'], userPermissions)) {
      out.push({ key: 'assign', label: 'Assign players to intern', icon: 'external-link', run: () => { setSelectedIntern(u); setAssignDialogOpen(true); } });
    }
    if (role === 'Athlete' && hasAnyPermission(['IS_ADMIN', 'IS_CACHE_AI'], userPermissions)) {
      out.push({ key: 'compute', label: 'Compute visibility score', hint: 'Re-pulls social metrics for this athlete', icon: 'refresh',
                 disabled: computingScoreEmail === u.email, run: () => handleComputeSocialScore(u) });
    }
    if (isAdminRow) {
      const notifyOn = u.notifyOnSignup !== false;
      out.push({ key: 'alerts', label: notifyOn ? 'Turn signup alerts off' : 'Turn signup alerts on', hint: 'Email this admin when someone signs up',
                 icon: notifyOn ? 'bell-off' : 'bell', run: () => handleToggleSignupNotify(u, !notifyOn) });
    }
    // A profile our team built moves to the athlete's own email, and a
    // transfer still waiting gets its claim link again. A locked account
    // waits until it is unlocked, as the server refuses it until then.
    if (role === 'Athlete' && canTransferAccounts && canTransfer(u)) {
      const pilotRow = isPilotRow(u);
      out.push({ key: 'transfer', label: pilotRow ? 'Activate and transfer' : 'Transfer account', icon: 'key',
                 hint: u.locked ? 'Unlock the account to transfer it'
                   : pilotRow ? 'Make this pilot profile live, with all its data, and send it to the athlete'
                     : "Move this profile to the athlete's own email",
                 disabled: !!u.locked, run: () => setTransferTarget(u) });
      if (transferPending(u) && !u.locked) {
        const to = u.transfer?.to || u.email;
        out.push({ key: 'claim-link', label: 'Copy claim link', hint: 'A fresh link for them to set a password', icon: 'copy',
                   run: () => handleCopyClaimLink(u) });
        out.push({ key: 'claim-email', label: 'Resend claim email', hint: `Emails the link to ${to} again`, icon: 'mail',
                   run: () => handleResendClaimEmail(u) });
      }
    }
    if (userPermissions.includes('IS_ADMIN') && !isSelf) {
      const locked = !!u.locked;
      out.push({
        key: 'lock',
        label: locked ? 'Unlock account' : 'Lock account',
        hint: locked ? `Locked${u.lockedBy ? ` by ${u.lockedBy}` : ''}. Lets them sign in again` : 'Blocks sign-in and ends their session within a minute',
        icon: locked ? 'unlock' : 'lock',
        tone: locked ? undefined : 'danger',
        confirm: {
          title: locked ? 'Unlock account' : 'Lock account',
          body: locked
            ? `Unlock ${who}? They can sign in again right away.`
            : `Lock ${who}? They are signed out within a minute and cannot sign in by password, mobile or Google until unlocked. Their data stays as it is.`,
          confirmLabel: locked ? 'Unlock' : 'Lock',
        },
        run: () => handleLockUser(u, !locked),
      });
    }
    if (hasAnyPermission(['IS_ADMIN'], userPermissions) && !isSelf) {
      out.push({ key: 'delete', label: 'Delete this user', hint: 'Asks you to type the email', icon: 'trash', tone: 'danger', run: () => requestDeleteUser(u) });
    }
    return out;
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'select',
        header: () => (
          <RowCheckbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={(next) => table.toggleAllPageRowsSelected(next)}
            label="Select every user on this page"
          />
        ),
        cell: ({ row }) => (
          <RowCheckbox
            checked={row.getIsSelected()}
            onChange={(next) => row.toggleSelected(next)}
            label={`Select ${row.original.name || row.original.email}`}
          />
        ),
        enableSorting: false,
      },
      { id: 'rowNumber', header: 'No', cell: () => null, enableSorting: false },
      {
        id: 'name',
        header: 'User',
        accessorKey: 'name',
        enableSorting: true,
        cell: ({ row }) => {
          const u = row.original;
          const avatarUrl = u.athlete?.avatar?.replace('/upload/', '/upload/w_48,h_48,c_fill,g_face,q_auto/');
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar name={u.name || u.email} src={avatarUrl || ''} size={32} />
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate bm-label-l text-bm-ink">{u.name || u.email}</span>
                  <CohortBadge row={u} />
                </div>
                <a
                  href={`mailto:${u.email}`}
                  title={u.email}
                  onClick={(e) => e.stopPropagation()}
                  className="block max-w-[200px] truncate bm-body-s text-bm-muted hover:text-bm-orange hover:underline"
                >
                  {u.email}
                </a>
              </div>
            </div>
          );
        },
      },
      {
        id: 'role',
        header: 'Role',
        accessorFn: (row) => calculateUserRole(row.permissions || []),
        enableSorting: true,
        cell: ({ getValue }) => <Badge tone={ROLE_TONE[getValue()] || 'outline'}>{getValue()}</Badge>,
      },
      {
        id: 'primarySport',
        header: 'Sport',
        accessorFn: sportOf,
        enableSorting: true,
        cell: ({ row, getValue }) => {
          const sport = getValue();
          const pos = positionOf(row.original);
          if (!sport) return <span className="text-bm-tertiary">-</span>;
          return <span>{sport}{pos ? <span className="text-bm-muted"> · {pos}</span> : null}</span>;
        },
      },
      {
        id: 'currentTeam',
        header: 'Team',
        accessorFn: teamOf,
        enableSorting: true,
        cell: ({ row, getValue }) => {
          const team = getValue();
          const school = schoolOf(row.original);
          return (
            <div className="min-w-0">
              <div className="truncate">{team || <span className="text-bm-tertiary">-</span>}</div>
              {school && school !== team && (
                <div className="truncate bm-body-s text-bm-muted" title="Educational history">{school}</div>
              )}
            </div>
          );
        },
      },
      {
        id: 'cacheScore',
        header: 'CacheScore',
        accessorKey: 'athlete.statistics.final_score',
        enableSorting: true,
        cell: ({ row }) => {
          if (!row.original.permissions.includes("IS_ATHLETE")) return null;
          const v = row.original.athlete?.statistics?.final_score;
          return v ? <span className="bm-label-l text-bm-ink">{v}</span> : <span className="text-bm-tertiary">-</span>;
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => ((row.permissions || []).includes('IS_ATHLETE') ? (row.athlete?.status || '') : (row.coachVerification?.status || '')),
        enableSorting: true,
        sortDescFirst: true,
        cell: ({ row }) => {
          const lockedBadge = row.original.locked ? (
            <Badge tone="critical" title={`Locked${row.original.lockedBy ? ` by ${row.original.lockedBy}` : ''}. Cannot sign in until unlocked.`}>Locked</Badge>
          ) : null;
          const v = verificationOf(row.original);
          if (!v) return lockedBadge;
          const isAthlete = (row.original.permissions || []).includes('IS_ATHLETE');
          let completion = null;
          if (isAthlete) {
            const { percentage, missingFields } = getProfileCompletionInfo(row.original.athlete || {});
            const stats = calculateStatisticsCompletion(row.original);
            const profileTitle = missingFields.length > 0 ? `Profile ${percentage}% complete. Missing: ${missingFields.join(', ')}` : `Profile ${percentage}% complete`;
            completion = <CompletionPair profile={percentage} stats={stats} profileTitle={profileTitle} className="mt-1" />;
          }
          return (
            <div className="min-w-0">
              <span className="inline-flex flex-wrap items-center gap-1" title={v.title}>
                {lockedBadge}
                <Badge tone={v.tone} dot={v.dot}>{v.label}{v.onboarding ? ' · OB' : ''}</Badge>
                {coachEmailUnproven(row.original) && (
                  <span title={EMAIL_UNPROVEN_HINT}><Badge tone="attention">{EMAIL_UNPROVEN_LABEL}</Badge></span>
                )}
                <TransferPendingBadge row={row.original} />
                <PlanBadge plan={plans[row.original.email]} admin compact />
              </span>
              {completion}
            </div>
          );
        },
      },
      {
        id: 'action',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          const role = calculateUserRole(u?.permissions || []);
          const isAdminRow = (u.permissions || []).includes('IS_ADMIN');
          const isSelf = u.email === myEmail;
          // Non-admin self rows show no actions; admin rows keep theirs so each
          // admin can manage their own signup alerts.
          if (!isAdminRow && isSelf) return null;
          const settings = settingsActionFor(u, role);
          return (
            <div className="flex items-center gap-1">
              {!isSelf && userPermissions.includes('IS_ADMIN') && (
                <button
                  type="button"
                  title="Open as this user (new tab)"
                  onClick={() => openUserAs(u)}
                  disabled={openingAs === u.email}
                  className="inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-full border border-bm-border px-2.5 bm-label-xs text-bm-ink transition-colors hover:border-bm-orange hover:text-bm-orange disabled:opacity-50"
                >
                  <Icon name="log-in" size={13} /> {openingAs === u.email ? 'Opening...' : 'Open as user'}
                </button>
              )}
              {settings && (
                <button type="button" title={settings.title} className={iconBtn} onClick={settings.run}>
                  <Icon name="settings" size={16} />
                </button>
              )}
              <button type="button" title="More actions" className={iconBtn} onClick={() => setActionsFor(u)} aria-haspopup="dialog">
                <Icon name="more-vertical" size={16} />
              </button>
            </div>
          );
        },
      },
    ];
    return baseColumns;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEmail, userPermissions, codes, navigateViewProfile, requestDeleteUser, openingAs, computingScoreEmail, plans]);

  const table = useReactTable({
    data: rowData,
    columns,
    pageCount: Math.ceil(totalCounts / limit),
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSelectedRowModel: () => {
      return table.getRowModel().rows.filter(row => rowSelection[row.id]);
    },
    getRowId: (row) => row.email,
    state: {
      columnFilters,
      sorting,
      rowSelection,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const finalScores = selectedRows
    .map(row => row.original.athlete?.statistics?.final_score)
    .filter(score => typeof score === 'number');

  const totalFinalScore = finalScores.reduce((acc, cur) => acc + cur, 0);
  const averageFinalScore = finalScores.length > 0 ? (totalFinalScore / finalScores.length).toFixed(2) : 0;

  const handlePermissionSave = async (user, newPermissions) => {
    const updatedUser = { ...user, permissions: newPermissions };
    try {
      await dispatch(updateUserPermission(updatedUser));
    } catch (err) {
      // The server's words, such as "Change roles from your own admin session."
      toast.error(err?.message || PERMISSION_SAVE_FAILED_MESSAGE);
      return;
    }
    toast.success("Your changes have been saved!");
    setRowData((prev) => prev.map((u) => (u.email === updatedUser.email ? updatedUser : u)));
  };

  const renderPagination = () => {
    const pageCount = Math.ceil(totalCounts / limit);
  
    if (pageCount <= 0) return null;

    return (
      <ReactPaginate
        previousLabel={"← Prev"}
        nextLabel={"Next →"}
        breakLabel={"..."}
        pageCount={pageCount}
        marginPagesDisplayed={1}
        pageRangeDisplayed={3}
        onPageChange={handlePageClick}
        {...USERS_PAGINATE_CLASSES}
        forcePage={Math.min(page - 1, pageCount - 1)}
      />
    );
  };

  // --- Render (V2 design system) ---
  const bucketPills = [
    { key: 'all',         label: 'All',                  count: bucketCounts.all },
    { key: 'athlete',     label: 'Athletes',             count: bucketCounts.athletes },
    { key: 'coach',       label: 'Coaches',              count: bucketCounts.coaches },
    // Admins pill is gated to admin / CACHE_AI requesters — the backend's
    // role=admin branch enforces the same gate.
    ...(canSeeAdmins ? [{ key: 'admin', label: 'Admins', count: bucketCounts.admins }] : []),
    { key: 'completed',   label: 'Completed',            count: bucketCounts.completed },
    { key: 'pending',     label: 'Pending verification', count: bucketCounts.pending },
    { key: 'uncompleted', label: 'Uncompleted',          count: bucketCounts.uncompleted },
  ];
  const cascadeSummary = cascadePlan
    ? [cascadeFilters.tier?.name, cascadeFilters.region?.name, cascadeFilters.body?.name,
      cascadeTeam ? displayTeamName(cascadeTeam) || cascadeTeam.name : ''].filter(Boolean).join(' / ')
    : '';
  const roleOf = (u) => calculateUserRole(u?.permissions || []);
  const spinner = <Loading label="Loading users..." />;
  const cellWidth = (id) => (
    id === 'select' ? 'w-[32px]' :
    id === 'rowNumber' ? 'w-[36px]' :
    id === 'name' ? 'max-w-[240px]' :
    id === 'role' ? 'w-[104px]' :
    id === 'primarySport' ? 'max-w-[130px]' :
    id === 'currentTeam' ? 'max-w-[170px]' :
    id === 'cacheScore' ? 'w-[84px]' :
    id === 'status' ? 'w-[124px]' :
    id === 'action' ? 'w-[196px]' :
    'max-w-[160px]'
  );

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        eyebrow="Admin Mode"
        title="Users"
        subtitle={`${bucketCounts.all ?? 0} accounts · ${bucketCounts.athletes ?? 0} athletes · ${bucketCounts.coaches ?? 0} coaches · ${bucketCounts.pending ?? 0} pending verification`}
        actions={canInviteAdmins ? (
          <Btn onClick={() => setInviteAdminOpen(true)}>
            <Icon name="shield" size={14} className="mr-1.5" /> Invite admin
          </Btn>
        ) : null}
      />

      {canReviewDuplicates && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SegmentedControl
            value={view}
            onChange={setView}
            options={[{ value: 'users', label: 'Users' }, { value: 'duplicates', label: 'Possible duplicates' }]}
            capitalize={false}
          />
        </div>
      )}

      {view === 'duplicates' && canReviewDuplicates ? (
        <DuplicatePairsPanel />
      ) : (
      <>
        {/* Scope + filters */}
        <Card padding="p-5" className="mb-4 w-full min-w-0 max-w-full">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="bm-label-xs text-bm-tertiary">Cohort</span>
              <SegmentedControl
                value={cohort}
                onChange={setCohort}
                options={[
                  { value: 'live', label: 'Live' },
                  { value: 'pilot', label: 'Pilot (legacy)' },
                  { value: 'archived', label: 'Archived' },
                  { value: 'all', label: 'All' },
                ]}
                capitalize={false}
              />
            </div>
            <div className="flex min-w-[240px] flex-1 items-center gap-2">
              <SearchBox
                value={inputValue}
                onChange={setInputValue}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setSearchQuery(inputValue); } }}
                placeholder="Search by name, email, sport or team, then press Enter"
                aria-label="Search users"
                className="flex-1"
              />
              <Btn variant="secondary" onClick={() => setSearchQuery(inputValue)}>Search</Btn>
              {hasAnyPermission(['IS_ADMIN', 'IS_CACHE_AI', 'IS_INTERN'], userPermissions) && (
                <Btn variant="secondary" onClick={handleDownloadClick} disabled={isExporting} title="Download users">
                  <Icon name="download" size={14} className="mr-1.5" /> {isExporting ? 'Exporting...' : 'Export'}
                </Btn>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {bucketPills.map((b) => (
              <Chip key={b.key} active={bucket === b.key} aria-pressed={bucket === b.key} count={b.count} onClick={() => setBucket(b.key)}>
                {b.label}
              </Chip>
            ))}
          </div>

          {/* Cascade filter (Sport > Tier > Region > Body > League > Team), collapsed by default. */}
          <div className="mt-4 rounded-[8px] border border-bm-border">
            <button
              type="button"
              onClick={() => setShowCascade((v) => !v)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
              aria-expanded={showCascade}
            >
              <span className="flex min-w-0 flex-col gap-[2px]">
                <span className="bm-label-l text-bm-ink">Filter by program</span>
                <span className="truncate bm-body-s text-bm-muted">
                  {cascadeSummary ? `Filtering: ${cascadeSummary}` : 'Sport, tier, region, league, then team'}
                </span>
              </span>
              <span className="bm-label-xs text-bm-orange">{showCascade ? 'Hide' : 'Show'}</span>
            </button>
            {showCascade && (
              <div className="border-t border-bm-border px-4 pb-5 pt-4">
                {/* The V2 cascade: same filter state and reset rules as the
                    legacy CacheShareFilters, drawn for a white card. */}
                <CascadePicker
                  filters={cascadeFilters}
                  onFiltersChange={setCascadeFilters}
                  teams={cascadeTeams}
                  selectedTeam={cascadeTeam}
                  onTeamSelect={setCascadeTeam}
                  isLoadingTeams={cascadeTeamsLoading}
                  layout="modal"
                  variant="v2"
                />
                {(cascadeFilters.sport || cascadeTeam) && (
                  <Btn variant="link" onClick={clearCascade} className="mt-3">Clear program filters</Btn>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Toolbar: selection summary, pagination, page size */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="bm-body-s text-bm-muted">
            {selectedRows.length > 0
              ? `${selectedRows.length} selected · total CacheScore ${totalFinalScore} · average ${averageFinalScore}`
              : `Page ${page} of ${Math.max(1, Math.ceil(totalCounts / limit))}`}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {renderPagination()}
            <label className="flex items-center gap-2 bm-body-s text-bm-muted">
              Rows
              <Select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="!h-8 !w-auto !py-0 bm-label-xs">
                {[5, 10, 20, 50, 100, 200, 500, 1000].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </Select>
            </label>
          </div>
        </div>

        {/* Table (sm and up) */}
        <Card padding="p-0" className="hidden w-full min-w-0 max-w-full overflow-hidden sm:block">
          {loading ? spinner : rowData.length === 0 ? (
            <EmptyState className="m-6">No users match these filters.</EmptyState>
          ) : (
            <div className="max-h-[70vh] w-full max-w-full overflow-auto">
              <table className="w-full text-left">
                <thead className="sticky top-0 z-10 bg-bm-subtle">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`cursor-pointer select-none whitespace-nowrap border-b border-bm-border px-2.5 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted ${cellWidth(header.column.id)}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <span className="text-bm-orange">{{ asc: '↑', desc: '↓' }[header.column.getIsSorted()] ?? ''}</span>
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, i) => (
                    <tr key={row.id} className={`border-b border-bm-border transition-colors hover:bg-bm-subtle ${row.getIsSelected() ? 'bg-bm-subtle' : 'bg-white'}`}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={`overflow-hidden text-ellipsis whitespace-nowrap px-2.5 py-2.5 bm-body-m text-bm-ink ${cellWidth(cell.column.id)}`}>
                          {cell.column.id === 'rowNumber'
                            ? <span className="text-bm-tertiary">{(page - 1) * limit + i + 1}</span>
                            : flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Cards (phones) */}
        <div className="sm:hidden">
          {loading ? spinner : (
            <div className="flex flex-col gap-3">
              {(() => {
                const visibleUsers = table.getRowModel().rows.map((row) => row.original);
                const allVisibleSelected = visibleUsers.length > 0 && visibleUsers.every((u) => rowSelection[u.email]);
                const someVisibleSelected = !allVisibleSelected && visibleUsers.some((u) => rowSelection[u.email]);
                return (
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={(checked) => {
                      setRowSelection((prev) => {
                        const updated = { ...prev };
                        visibleUsers.forEach((u) => { updated[u.email] = checked; });
                        return updated;
                      });
                    }}
                    label="Select all on this page"
                    className="px-1"
                  />
                );
              })()}
              {rowData.map((u) => {
                const roleName = roleOf(u);
                const isAthlete = roleName === 'Athlete';
                const isSelf = u.email === myEmail;
                const positions = codes.find((c) => c.type === 'sport' && c.value === u.athlete?.primarySport)?.positions || [];
                const posArray = Array.isArray(u.athlete?.position) ? u.athlete.position : (u.athlete?.position != null ? [u.athlete.position] : []);
                const posLabel = posArray.map((v) => extractPositionAbbreviation(positions.find((pp) => pp.value === v)?.name || '')).filter(Boolean).join(', ');
                const profilePct = calculateProfileCompletion(u.athlete || {});
                const statsPct = calculateStatisticsCompletion(u);
                return (
                  <Card key={u.email} padding="p-4">
                    <div className="flex items-start gap-3">
                      <Avatar name={u.name || u.email} src={u.athlete?.avatar || ''} size={44} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate bm-label-l text-bm-ink">{u.name || u.email}</span>
                          <Badge tone={ROLE_TONE[roleName] || 'outline'}>{roleName}</Badge>
                          <CohortBadge row={u} />
                          <TransferPendingBadge row={u} />
                          <PlanBadge plan={plans[u.email]} admin compact />
                        </div>
                        <div className="truncate bm-body-s text-bm-muted">{u.email}</div>
                        {isAthlete && (
                          <div className="mt-2 flex flex-col gap-[2px] bm-body-s text-bm-muted">
                            <span>Sport: {codeName('sport', u.athlete?.primarySport) || '-'}{posLabel ? ` · ${posLabel}` : ''}</span>
                            <span>Team: {teamOf(u) || '-'}</span>
                            <span>CacheScore: {u.athlete?.statistics?.final_score || '-'}</span>
                            <span className="mt-1 flex gap-1.5">
                              <Badge tone={pctTone(profilePct)} dot={pctDot(profilePct)}>Profile {profilePct}%</Badge>
                              <Badge tone={pctTone(statsPct)} dot={pctDot(statsPct)}>Stats {statsPct}%</Badge>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      {!isSelf && userPermissions.includes('IS_ADMIN') && (
                        <Btn size="sm" variant="secondary" onClick={() => openUserAs(u)} disabled={openingAs === u.email}>
                          <Icon name="log-in" size={13} className="mr-1" /> {openingAs === u.email ? 'Opening...' : 'Open as user'}
                        </Btn>
                      )}
                      {(() => { const st = settingsActionFor(u, roleName); return st ? (
                        <button type="button" title={st.title} className={iconBtn} onClick={st.run}><Icon name="settings" size={16} /></button>
                      ) : null; })()}
                      <button type="button" title="More actions" className={iconBtn} onClick={() => setActionsFor(u)}><Icon name="more-vertical" size={16} /></button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </>
      )}

      {actionsFor && (
        <RowActionsModal
          user={actionsFor}
          roleName={calculateUserRole(actionsFor.permissions || [])}
          actions={buildRowActions(actionsFor)}
          onClose={() => setActionsFor(null)}
          plan={plans[actionsFor.email]}
        />
      )}

      <PermissionDialog
        open={dialogOpen}
        user={selectedUser}
        permissions={permissions}
        onClose={() => setDialogOpen(false)}
        onSave={handlePermissionSave}
      />

      <CoachTeamsDialog
        open={coachTeamsDialogOpen}
        coach={coachTeamsTarget}
        onClose={() => { setCoachTeamsDialogOpen(false); setCoachTeamsTarget(null); }}
        onChange={() => {
          // Re-fetch the page so the row's coachVerification.additionalTeams
          // reflects the add/remove.
          dispatch(fetchAllUsers({
            page, limit, search: searchQuery,
            ...bucketToParams[bucket],
            cohort,
          }));
        }}
      />

      <AssignAthleteTeamDialog
        open={assignTeamOpen}
        athlete={assignTeamTarget?.athlete
          ? { ...assignTeamTarget.athlete, email: assignTeamTarget.email,
              name: assignTeamTarget.athlete.name, surname: assignTeamTarget.athlete.surname }
          : assignTeamTarget}
        currentTeamName={assignTeamTarget ? teamOf(assignTeamTarget) : ''}
        initialMode={assignTeamMode || undefined}
        onClose={() => { setAssignTeamOpen(false); setAssignTeamTarget(null); setAssignTeamMode(null); }}
        onChange={() => {
          // Assigning a team can mint a new team code, and the table resolves
          // names from the codes list.
          dispatch(fetchAllCodes());
          dispatch(fetchAllUsers({
            page, limit, search: searchQuery,
            ...bucketToParams[bucket],
            cohort,
          }));
        }}
      />

      <LinkedCerebroRecordsDialog
        open={!!linkedRecordsTarget}
        athlete={linkedRecordsTarget
          ? { email: linkedRecordsTarget.email, name: linkedRecordsTarget.athlete?.name,
              surname: linkedRecordsTarget.athlete?.surname }
          : null}
        onClose={() => setLinkedRecordsTarget(null)}
        onChange={() => {
          // A link change refreshes the athlete's stats and stored scores.
          dispatch(fetchAllUsers({
            page, limit, search: searchQuery,
            ...bucketToParams[bucket],
            cohort,
          }));
        }}
      />

      {transferTarget && (
        <TransferAccountDialog
          key={transferTarget.email}
          user={transferTarget}
          onClose={() => setTransferTarget(null)}
          onTransferred={(data) => handleTransferred(transferTarget, data)}
        />
      )}

      {claimLinkShown && (
        <ClaimLinkDialog
          email={claimLinkShown.email}
          link={claimLinkShown.link}
          emailFailed={!!claimLinkShown.emailFailed}
          onClose={() => setClaimLinkShown(null)}
        />
      )}

      {unverifyTarget && (
        <BmModal
          title="Revoke verification"
          onClose={() => !unverifying && setUnverifyTarget(null)}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => setUnverifyTarget(null)} disabled={unverifying}>Cancel</Btn>
              <Btn onClick={confirmUnverifyCoach} disabled={unverifying} className="!bg-bm-critical hover:!bg-[#c41d14]">
                {unverifying ? 'Revoking...' : 'Yes, revoke'}
              </Btn>
            </>
          )}
        >
          <p className="bm-body-m text-bm-ink">
            Revoke <span className="font-semibold">{unverifyTarget.name || unverifyTarget.email}</span>'s verification?
          </p>
          <p className="mt-2 bm-body-s text-bm-muted">
            Their 6-character team code stops working immediately. Athletes who linked with that code lose verified status
            and return to unverified. The coach record stays, so you can re-verify later.
          </p>
        </BmModal>
      )}

      <AssignDialog
        open={assignDialogOpen}
        intern={selectedIntern}
        onClose={() => setAssignDialogOpen(false)}
        onAssign={handleAssignInterns}
        previousSelection={previousAssignSelection}
        setPreviousSelection={setPreviousAssignSelection}
      />

      <InviteAdminDialog
        open={inviteAdminOpen}
        onClose={() => setInviteAdminOpen(false)}
        onInvited={() => {
          // Refetch so the new admin shows up immediately, on the Admins pill.
          setBucket('admin');
          dispatch(fetchAllUsers({
            page: 1, limit, search: searchQuery,
            ...bucketToParams.admin,
            leagues: cascadeQuery.leagues,
            team_id: cascadeQuery.team_id,
            cohort,
          }));
        }}
      />

      {pendingDelete && (
        <BmModal
          title="Delete user account"
          onClose={() => !deletingNow && cancelDeleteUser()}
          footer={(
            <>
              <Btn variant="secondary" onClick={cancelDeleteUser} disabled={deletingNow}>Cancel</Btn>
              <Btn
                onClick={confirmDeleteUser}
                disabled={deletingNow || (deleteConfirmText || '').trim().toLowerCase() !== (pendingDelete.email || '').toLowerCase()}
                className="!bg-bm-critical hover:!bg-[#c41d14]"
              >
                {deletingNow ? 'Deleting...' : 'Delete account'}
              </Btn>
            </>
          )}
        >
          <div className="flex items-center gap-3">
            <Avatar name={pendingDelete.name || pendingDelete.email} src={pendingDelete.athlete?.avatar || ''} size={40} />
            <div className="min-w-0">
              <div className="truncate bm-label-l text-bm-ink">
                {pendingDelete.name || `${pendingDelete.firstName || ''} ${pendingDelete.lastName || ''}`.trim() || pendingDelete.email}
              </div>
              <div className="truncate bm-body-s text-bm-muted">{pendingDelete.email}</div>
            </div>
            {(pendingDelete.permissions || []).length > 0 && (
              <Badge tone={ROLE_TONE[roleOf(pendingDelete)] || 'outline'} className="ml-auto">{roleOf(pendingDelete)}</Badge>
            )}
          </div>
          <p className="mt-4 bm-body-s text-bm-muted">
            This removes the account, its profile and its history. It cannot be undone.
            Type <span className="font-mono font-semibold text-bm-ink">{pendingDelete.email}</span> to confirm.
          </p>
          <Input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={pendingDelete.email}
            className="mt-3"
            autoFocus
          />
        </BmModal>
      )}

      {showUploadTypeDialog && (
        <BmModal
          title="Select level"
          onClose={() => { setShowUploadTypeDialog(false); setUploadLevel(null); }}
          width={420}
          footer={(
            <>
              <Btn variant="secondary" onClick={() => { setShowUploadTypeDialog(false); setUploadLevel(null); }}>Cancel</Btn>
              <Btn disabled={!uploadLevel} onClick={() => {
                handleUploadToEndpoint('/api/users/coach/excel/upload/', uploadLevel);
                setShowUploadTypeDialog(false);
                setUploadLevel(null);
              }}>Upload</Btn>
            </>
          )}
        >
          <div className="flex flex-col gap-2">
            {[['highschool', 'High School'], ['college', 'College / University'], ['pro', 'Dev / Pro']].map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 bm-body-m text-bm-ink">
                <input type="radio" name="uploadLevel" value={value} checked={uploadLevel === value} onChange={() => setUploadLevel(value)} className="accent-bm-orange" />
                {label}
              </label>
            ))}
          </div>
        </BmModal>
      )}

      {/* Download options */}
      {downloadOpen && (
        <BmModal
          title="Download users"
          onClose={cancelDownload}
          width={560}
          footer={(
            <>
              <Btn variant="secondary" onClick={cancelDownload}>Cancel</Btn>
              <Btn onClick={handleDownloadConfirm} disabled={!downloadType}>Download</Btn>
            </>
          )}
        >
          <p className="bm-body-s text-bm-muted">Pick what to export and how to filter it. CSVs include emails.</p>
          <div className="mt-5 space-y-6">
            <div>
              <div className="mb-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">What to download</div>
              <div className="space-y-2">
                {[
                  { value: 'sample', label: 'Sample template', desc: 'Blank CSV showing every column we ingest.' },
                  { value: 'profiles', label: 'Full profiles', desc: 'Email, contact, team, status, coach. Honors the filters below.' },
                  { value: 'athlete-profiles', label: 'CacheScore and CacheValue', desc: 'One row per athlete with score and value range.' },
                ].map((opt) => {
                  const selected = downloadType === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-[8px] border px-4 py-3 transition-colors ${
                        selected ? 'border-bm-orange bg-bm-subtle' : 'border-bm-border bg-white hover:border-bm-ink'
                      }`}
                    >
                      <input type="radio" name="download-type" value={opt.value} checked={selected} onChange={(e) => setDownloadType(e.target.value)} className="mt-0.5 accent-bm-orange" />
                      <div className="flex-1">
                        <div className="bm-label-l text-bm-ink">{opt.label}</div>
                        <div className="mt-0.5 bm-body-s text-bm-muted">{opt.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {downloadType === 'profiles' && (
              <>
                <div>
                  <div className="mb-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">Role</div>
                  <div className="flex flex-wrap gap-2">
                    {[{ value: 'all', label: 'All' }, { value: 'athletes', label: 'Athletes' }, { value: 'coaches', label: 'Coaches' }].map((r) => (
                      <Chip key={r.value} active={downloadRole === r.value} aria-pressed={downloadRole === r.value} onClick={() => setDownloadRole(r.value)}>{r.label}</Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="mb-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">Status</div>
                  <div className="flex flex-wrap gap-2">
                    {[{ value: '', label: 'Any' }, { value: 'verified', label: 'Completed' }, { value: 'unverified', label: 'Pending verification' }, { value: 'uncompleted', label: 'Uncompleted' }].map((st) => (
                      <Chip key={st.value || 'any'} active={downloadStatus === st.value} aria-pressed={downloadStatus === st.value} onClick={() => setDownloadStatus(st.value)}>{st.label}</Chip>
                    ))}
                  </div>
                </div>
                {downloadRole !== 'coaches' && (
                  <div>
                    <div className="mb-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">School or team <span className="normal-case text-bm-tertiary">optional</span></div>
                    <Select aria-label="School or team" value={selectedTeamFilter} onChange={(e) => setSelectedTeamFilter(e.target.value)}>
                      <option value="">All schools and teams</option>
                      {teamOptions.map((team) => (<option key={team.value} value={team.value}>{team.label}</option>))}
                    </Select>
                  </div>
                )}
              </>
            )}

            {downloadType === 'athlete-profiles' && (
              <div>
                <div className="mb-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">School or team <span className="normal-case text-bm-tertiary">optional</span></div>
                <Select aria-label="School or team" value={selectedTeamFilter} onChange={(e) => setSelectedTeamFilter(e.target.value)}>
                  <option value="">All schools and teams</option>
                  {teamOptions.map((team) => (<option key={team.value} value={team.value}>{team.label}</option>))}
                </Select>
              </div>
            )}
          </div>
        </BmModal>
      )}
    </div>
  );
};

export default AdminUserManager;
